import type {
  SchemaField,
  CompatibilityMode,
  SchemaValidationError,
  FieldType,
} from '../types/schema.types';

/**
 * Reserved SQL keywords that cannot be used as field names
 */
const SQL_RESERVED_KEYWORDS = new Set([
  'SELECT',
  'FROM',
  'WHERE',
  'INSERT',
  'UPDATE',
  'DELETE',
  'CREATE',
  'DROP',
  'ALTER',
  'TABLE',
  'INDEX',
  'VIEW',
  'JOIN',
  'LEFT',
  'RIGHT',
  'INNER',
  'OUTER',
  'ON',
  'AS',
  'AND',
  'OR',
  'NOT',
  'NULL',
  'IS',
  'IN',
  'BETWEEN',
  'LIKE',
  'ORDER',
  'BY',
  'GROUP',
  'HAVING',
  'DISTINCT',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
]);

/**
 * Common PII field name patterns
 * Used to detect potential PII fields for warning users
 */
const PII_PATTERNS = [
  // Personal identifiers
  /\b(ssn|social_?security|tax_?id)\b/i,
  /\b(email|e_?mail|mail_?address)\b/i,
  /\b(phone|telephone|mobile|cell)\b/i,
  /\b(passport|driver_?license|license_?number)\b/i,

  // Names
  /\b(first_?name|last_?name|full_?name|surname|given_?name)\b/i,

  // Location
  /\b(address|street|city|state|zip|postal|country)\b/i,
  /\b(latitude|longitude|lat_?lon|geo_?location)\b/i,

  // Financial
  /\b(credit_?card|card_?number|cvv|account_?number|routing_?number)\b/i,
  /\b(salary|income|wage|compensation)\b/i,

  // Health
  /\b(medical|health|diagnosis|prescription|patient)\b/i,

  // Biometric
  /\b(fingerprint|facial|biometric|retina|iris)\b/i,

  // IP addresses
  /\b(ip_?address|ip_?addr|client_?ip)\b/i,
];

/**
 * Valid field types
 */
const VALID_FIELD_TYPES: Set<FieldType> = new Set([
  'string',
  'integer',
  'bigint',
  'float',
  'double',
  'boolean',
  'timestamp',
  'date',
  'binary',
  'array',
  'map',
  'struct',
  'null',
]);

/**
 * Validate schema name
 *
 * Checks that schema name:
 * - Is not empty
 * - Contains only valid characters (alphanumeric, underscore, hyphen)
 * - Starts with a letter
 * - Is not a SQL reserved keyword
 * - Has reasonable length (3-64 characters)
 *
 * @param name - Schema name to validate
 * @returns Array of validation errors (empty if valid)
 *
 * @example
 * ```ts
 * const errors = validateSchemaName('user_events');
 * if (errors.length > 0) {
 *   console.error('Invalid schema name:', errors);
 * }
 * ```
 */
export function validateSchemaName(name: string): SchemaValidationError[] {
  const errors: SchemaValidationError[] = [];

  // Check if empty
  if (!name || name.trim().length === 0) {
    errors.push({
      field: 'name',
      message: 'Schema name is required',
      code: 'NAME_REQUIRED',
    });
    return errors;
  }

  const trimmedName = name.trim();

  // Check length
  if (trimmedName.length < 3) {
    errors.push({
      field: 'name',
      message: 'Schema name must be at least 3 characters long',
      code: 'NAME_TOO_SHORT',
    });
  }

  if (trimmedName.length > 64) {
    errors.push({
      field: 'name',
      message: 'Schema name must not exceed 64 characters',
      code: 'NAME_TOO_LONG',
    });
  }

  // Check if starts with letter
  if (!/^[a-zA-Z]/.test(trimmedName)) {
    errors.push({
      field: 'name',
      message: 'Schema name must start with a letter',
      code: 'NAME_INVALID_START',
    });
  }

  // Check valid characters - unified pattern: only letters, numbers, and underscores (no hyphens)
  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmedName)) {
    errors.push({
      field: 'name',
      message: 'Schema name must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)',
      code: 'NAME_INVALID_CHARACTERS',
    });
  }

  // Check for reserved keywords
  if (SQL_RESERVED_KEYWORDS.has(trimmedName.toUpperCase())) {
    errors.push({
      field: 'name',
      message: `"${trimmedName}" is a reserved SQL keyword and cannot be used as a schema name`,
      code: 'NAME_RESERVED_KEYWORD',
    });
  }

  return errors;
}

/**
 * Validate schema fields
 *
 * Checks that fields:
 * - Array is not empty
 * - Each field has required properties
 * - Field names are valid and unique
 * - Field types are supported
 * - No duplicate field names exist
 *
 * @param fields - Array of schema fields to validate
 * @returns Array of validation errors (empty if valid)
 *
 * @example
 * ```ts
 * const errors = validateFields([
 *   { name: 'user_id', type: 'string', nullable: false },
 *   { name: 'created_at', type: 'timestamp', nullable: false }
 * ]);
 * ```
 */
export function validateFields(fields: SchemaField[]): SchemaValidationError[] {
  const errors: SchemaValidationError[] = [];
  const fieldNames = new Set<string>();

  // Check if fields array is empty
  if (!fields || fields.length === 0) {
    errors.push({
      message: 'Schema must have at least one field',
      code: 'FIELDS_EMPTY',
    });
    return errors;
  }

  // Validate each field
  fields.forEach((field, index) => {
    const fieldPrefix = `fields[${index}]`;

    // Check field name
    if (!field.name || field.name.trim().length === 0) {
      errors.push({
        field: fieldPrefix,
        message: `Field at position ${index + 1} must have a name`,
        code: 'FIELD_NAME_REQUIRED',
      });
    } else {
      const trimmedName = field.name.trim();

      // Check field name format
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmedName)) {
        errors.push({
          field: fieldPrefix,
          message: `Field "${trimmedName}" has invalid format. Must start with letter and contain only letters, numbers, and underscores`,
          code: 'FIELD_NAME_INVALID',
        });
      }

      // Check for duplicate names
      if (fieldNames.has(trimmedName.toLowerCase())) {
        errors.push({
          field: fieldPrefix,
          message: `Duplicate field name "${trimmedName}"`,
          code: 'FIELD_NAME_DUPLICATE',
        });
      }
      fieldNames.add(trimmedName.toLowerCase());

      // Check for reserved keywords
      if (SQL_RESERVED_KEYWORDS.has(trimmedName.toUpperCase())) {
        errors.push({
          field: fieldPrefix,
          message: `Field name "${trimmedName}" is a reserved SQL keyword`,
          code: 'FIELD_NAME_RESERVED',
        });
      }
    }

    // Check field type
    if (!field.type) {
      errors.push({
        field: fieldPrefix,
        message: `Field "${field.name}" must have a type`,
        code: 'FIELD_TYPE_REQUIRED',
      });
    } else if (!VALID_FIELD_TYPES.has(field.type)) {
      errors.push({
        field: fieldPrefix,
        message: `Field "${field.name}" has invalid type "${field.type}"`,
        code: 'FIELD_TYPE_INVALID',
      });
    }

    // Check nullable is boolean
    if (typeof field.nullable !== 'boolean') {
      errors.push({
        field: fieldPrefix,
        message: `Field "${field.name}" must specify nullable as true or false`,
        code: 'FIELD_NULLABLE_REQUIRED',
      });
    }
  });

  return errors;
}

/**
 * Detect potential PII fields
 *
 * Scans field names for common PII patterns and returns list of
 * field names that may contain personally identifiable information.
 *
 * This is used to warn users about PII data and prompt for appropriate
 * handling (encryption, masking, etc.)
 *
 * @param fields - Array of schema fields to scan
 * @returns Array of field names that may contain PII
 *
 * @example
 * ```ts
 * const fields = [
 *   { name: 'user_id', type: 'string', nullable: false },
 *   { name: 'email', type: 'string', nullable: false },
 *   { name: 'created_at', type: 'timestamp', nullable: false }
 * ];
 * const piiFields = detectPIIFields(fields);
 * // Returns: ['email']
 * ```
 */
export function detectPIIFields(fields: SchemaField[]): string[] {
  const piiFields: string[] = [];

  fields.forEach((field) => {
    const fieldName = field.name.toLowerCase();

    // Check if field name matches any PII pattern
    const isPII = PII_PATTERNS.some((pattern) => pattern.test(fieldName));

    if (isPII || field.isPII === true) {
      piiFields.push(field.name);
    }
  });

  return piiFields;
}

/**
 * Validate compatibility mode
 *
 * Checks that the provided compatibility mode is valid.
 *
 * @param mode - Compatibility mode to validate
 * @returns True if valid, false otherwise
 *
 * @example
 * ```ts
 * if (!validateCompatibilityMode('backward')) {
 *   throw new Error('Invalid compatibility mode');
 * }
 * ```
 */
export function validateCompatibilityMode(mode: string): mode is CompatibilityMode {
  return ['backward', 'forward', 'full', 'none'].includes(mode);
}

/**
 * Validate complete schema for creation
 *
 * Performs comprehensive validation of all schema properties
 * including name, fields, and compatibility mode.
 *
 * @param schema - Schema object to validate
 * @param schema.name - Schema name
 * @param schema.fields - Schema fields
 * @param schema.compatibility_mode - Compatibility mode
 * @returns Object with validation result and errors
 *
 * @example
 * ```ts
 * const validation = validateSchema({
 *   name: 'user_events',
 *   fields: [...],
 *   compatibility_mode: 'backward'
 * });
 *
 * if (!validation.isValid) {
 *   console.error('Validation errors:', validation.errors);
 * }
 * ```
 */
export function validateSchema(schema: {
  name: string;
  fields: SchemaField[];
  compatibility_mode: CompatibilityMode;
}): {
  isValid: boolean;
  errors: SchemaValidationError[];
  warnings: { message: string; fields: string[] }[];
} {
  const errors: SchemaValidationError[] = [];
  const warnings: { message: string; fields: string[] }[] = [];

  // Validate name
  errors.push(...validateSchemaName(schema.name));

  // Validate fields
  errors.push(...validateFields(schema.fields));

  // Validate compatibility mode
  if (!validateCompatibilityMode(schema.compatibility_mode)) {
    errors.push({
      field: 'compatibility_mode',
      message: `Invalid compatibility mode "${schema.compatibility_mode}". Must be backward, forward, full, or none`,
      code: 'COMPATIBILITY_MODE_INVALID',
    });
  }

  // Detect PII fields for warnings
  const piiFields = detectPIIFields(schema.fields);
  if (piiFields.length > 0) {
    warnings.push({
      message: 'The following fields may contain personally identifiable information (PII)',
      fields: piiFields,
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Check field compatibility for schema evolution
 *
 * Determines if a field change is compatible with the specified
 * compatibility mode. Used when creating new schema versions.
 *
 * @param oldField - Original field definition
 * @param newField - Updated field definition
 * @param mode - Compatibility mode to check against
 * @returns Object indicating if change is compatible and any errors
 *
 * @example
 * ```ts
 * const result = checkFieldCompatibility(
 *   { name: 'user_id', type: 'string', nullable: false },
 *   { name: 'user_id', type: 'string', nullable: true },
 *   'backward'
 * );
 *
 * if (!result.isCompatible) {
 *   console.error('Incompatible change:', result.reason);
 * }
 * ```
 */
export function checkFieldCompatibility(
  oldField: SchemaField,
  newField: SchemaField,
  mode: CompatibilityMode
): { isCompatible: boolean; reason?: string } {
  // NONE mode allows any changes
  if (mode === 'none') {
    return { isCompatible: true };
  }

  // Field type changed
  if (oldField.type !== newField.type) {
    return {
      isCompatible: false,
      reason: `Type change from ${oldField.type} to ${newField.type} breaks compatibility`,
    };
  }

  // BACKWARD: New schema can read old data
  if (mode === 'backward' || mode === 'full') {
    // Making field nullable is backward compatible (can read old non-null data)
    // Making field non-nullable is NOT backward compatible (can't read old null data)
    if (oldField.nullable && !newField.nullable) {
      return {
        isCompatible: false,
        reason: 'Making a nullable field non-nullable breaks backward compatibility',
      };
    }
  }

  // FORWARD: Old schema can read new data
  if (mode === 'forward' || mode === 'full') {
    // Making field non-nullable is forward compatible (old schema can read new non-null data)
    // Making field nullable is NOT forward compatible (old schema can't read new null data)
    if (!oldField.nullable && newField.nullable) {
      return {
        isCompatible: false,
        reason: 'Making a non-nullable field nullable breaks forward compatibility',
      };
    }
  }

  return { isCompatible: true };
}
